import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { payoutApi } from '../api/client';

// ── Admin: config ────────────────────────────────────────────────────────────

export function usePayoutConfig() {
  return useQuery({
    queryKey: ['admin', 'payoutConfig'],
    queryFn: async () => {
      const { data } = await payoutApi.getConfig();
      if (!data.success) throw new Error(data.message || 'Failed to load payout configuration');
      return data.data;
    },
  });
}

export function useUpdatePayoutConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (config) => payoutApi.updateConfig(config),
    onSuccess: () => qc.invalidateQueries(['admin', 'payoutConfig']),
  });
}

// ── Admin: catalog tier ──────────────────────────────────────────────────────

export function useSetPayoutTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ movieId, tier }) => payoutApi.setTier(movieId, tier),
    onSuccess: () => qc.invalidateQueries(['admin', 'catalog']),
  });
}

export function useRevenueSuggestion(year, month) {
  return useQuery({
    queryKey: ['admin', 'revenueSuggestion', year, month],
    queryFn: async () => {
      const { data } = await payoutApi.getRevenueSuggestion(year, month);
      if (!data.success) throw new Error(data.message || 'Failed to load revenue suggestion');
      return data.data;
    },
    enabled: !!year && !!month,
  });
}

// ── Admin: monthly runs ──────────────────────────────────────────────────────

export function useRunPayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => payoutApi.runPayout(payload),
    onSuccess: (_, { year, month }) => {
      qc.invalidateQueries(['admin', 'payoutRun', year, month]);
      qc.invalidateQueries(['admin', 'payoutRuns']);
    },
  });
}

export function usePayoutRun(year, month) {
  return useQuery({
    queryKey: ['admin', 'payoutRun', year, month],
    queryFn: async () => {
      const { data } = await payoutApi.getRun(year, month);
      if (!data.success) throw new Error(data.message || 'No run for that period yet');
      return data.data;
    },
    enabled: !!year && !!month,
    retry: false,
  });
}

export function usePayoutRuns() {
  return useQuery({
    queryKey: ['admin', 'payoutRuns'],
    queryFn: async () => {
      const { data } = await payoutApi.listRuns();
      if (!data.success) throw new Error(data.message || 'Failed to load payout runs');
      return data.data || [];
    },
  });
}

// ── Producer dashboard ───────────────────────────────────────────────────────

export function useMyPayouts(year, month) {
  return useQuery({
    queryKey: ['myPayouts', year, month],
    queryFn: async () => {
      const { data } = await payoutApi.myPayouts(year, month);
      if (!data.success) throw new Error(data.message || 'Failed to load your payouts');
      return data;
    },
    enabled: !!year && !!month,
  });
}
